import { supabase } from '@/integrations/supabase/client';
import { RentalWithRelations, BranchPerformanceData, ShiftWithRelations, BranchWeeklySales, WeeklyAdminStats } from '@/types/models';
import { startOfMonth, endOfMonth, subMonths, isWithinInterval } from 'date-fns';

// Dashboard
export const getBranchPerformance = async (): Promise<BranchPerformanceData[]> => {
  const today = new Date();
  const startOfCurrentMonth = startOfMonth(today);
  const startOfPreviousMonth = startOfMonth(subMonths(today, 1));
  const endOfPreviousMonth = endOfMonth(subMonths(today, 1));

  const { data: shifts, error } = await supabase
    .from('shifts')
    .select(`
      start_time,
      amount_admin,
      total_rentals,
      operator:profiles (
        branch:branch_id (id, name)
      )
    `)
    // Fetch all shifts from the beginning of last month until now
    .eq('is_deleted', false)
    .is('deleted_at', null)
    .gte('start_time', startOfPreviousMonth.toISOString());

  if (error) {
    console.error("Error fetching shifts for branch performance:", error);
    throw error;
  }

  if (!shifts) {
    return [];
  }

  const performanceByBranch: Record<string, BranchPerformanceData> = {};

  const typedShifts = shifts as unknown as {
      start_time: string;
      amount_admin: number;
      total_rentals: number | null;
      operator: {
          branch: {
              id: string;
              name: string;
          } | null;
      } | null;
  }[];

  for (const shift of typedShifts) {
    const branch = shift.operator?.branch;
    if (branch && branch.id && branch.name) {
      const { id: branchId, name: branchName } = branch;

      // Initialize the branch data if it's not there yet
      if (!performanceByBranch[branchId]) {
        performanceByBranch[branchId] = {
          branchId,
          branchName,
          currentMonthProfit: 0,
          currentMonthRentals: 0,
          previousMonthProfit: 0,
          previousMonthRentals: 0,
        };
      }
      
      const shiftDate = new Date(shift.start_time);

      // Check which period the shift belongs to and aggregate
      if (isWithinInterval(shiftDate, { start: startOfCurrentMonth, end: today })) {
        performanceByBranch[branchId].currentMonthProfit += shift.amount_admin || 0;
        performanceByBranch[branchId].currentMonthRentals += shift.total_rentals || 0;
      } else if (isWithinInterval(shiftDate, { start: startOfPreviousMonth, end: endOfPreviousMonth })) {
        performanceByBranch[branchId].previousMonthProfit += shift.amount_admin || 0;
        performanceByBranch[branchId].previousMonthRentals += shift.total_rentals || 0;
      }
    }
  }

  return Object.values(performanceByBranch);
};

//Reports
export const getRentalsByBranchAndDate = async (branchId: string, date: Date): Promise<RentalWithRelations[]> => {
  const startDate = new Date(date);
  startDate.setHours(0, 0, 0, 0);
  const endDate = new Date(date);
  endDate.setHours(23, 59, 59, 999);

  const { data, error } = await supabase
    .from('rentals')
    .select(`
      *,
      customer:customer_id(id, name, phone),
      bike:bike_id(id, model, type, color, number),
      manager:manager_id(id, full_name),
      branch:branch_id(id, name, address),
      plan:plan_id(id, name, price, time_duration)
    `)
    .eq('branch_id', branchId)
    .gte('start_date', startDate.toISOString())
    .lte('start_date', endDate.toISOString())
    .order('start_date', { ascending: false });

  if (error) throw error;
  return data || [];
}

export const getShiftsByDateRange = async (branchId: string, startDate: Date, endDate: Date): Promise<ShiftWithRelations[]> => {
  // First, get the profiles (operators) for the given branch
  const { data: operators, error: operatorsError } = await supabase
    .from('profiles')
    .select('id')
    .eq('branch_id', branchId);

  if (operatorsError) throw operatorsError;
  if (!operators || operators.length === 0) return [];

  const operatorIds = operators.map(o => o.id);

  // Then, get the shifts for those operators in the given date range
  const { data, error } = await supabase
    .from('shifts')
    .select('*')
    .in('operator_id', operatorIds)
    .eq('is_deleted', false)
    .is('deleted_at', null)
    .gte('start_time', startDate.toISOString())
    .lte('start_time', endDate.toISOString())
    .order('start_time', { ascending: true });

  if (error) throw error;

  return data || [];
};

export const getShiftsByBranchAndMonth = async (branchId: string, month: string, year: string): Promise<ShiftWithRelations[]> => {
  const monthInt = parseInt(month, 10) - 1;
  const yearInt = parseInt(year, 10);

  const startDate = new Date(yearInt, monthInt, 1);
  startDate.setHours(0, 0, 0, 0);
  const endDate = new Date(yearInt, monthInt + 1, 0);
  endDate.setHours(23, 59, 59, 999);

  return getShiftsByDateRange(branchId, startDate, endDate);
};

export const getBranchWeeklySales = async (): Promise<BranchWeeklySales[]> => {
  const today = new Date();
  const dayOfWeek = today.getDay(); // Sunday = 0, Monday = 1, etc.
  const diff = today.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1); // Adjust for Sunday
  const monday = new Date(today.setDate(diff));
  monday.setHours(0, 0, 0, 0);

  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  sunday.setHours(23, 59, 59, 999);

  const { data: shifts, error } = await supabase
    .from('shifts')
    .select(`
      start_time,
      amount_admin,
      operator:profiles (
        branch:branch_id (id, name)
      )
    `)
    .eq('is_deleted', false)
    .is('deleted_at', null)
    .gte('start_time', monday.toISOString())
    .lte('start_time', sunday.toISOString());

  if (error) {
    console.error("Error fetching shifts for weekly sales:", error);
    throw error;
  }

  if (!shifts) {
    return [];
  }

  const salesByBranch: Record<string, Record<string, number>> = {};

  const typedShifts = shifts as unknown as {
    start_time: string;
    amount_admin: number;
    operator: {
      branch: {
        id: string;
        name: string;
      } | null;
    } | null;
  }[];

  for (const shift of typedShifts) {
    const branch = shift.operator?.branch;
    if (branch && branch.id && branch.name) {
      const { name: branchName } = branch;
      const shiftDate = new Date(shift.start_time);
      const day = shiftDate.toLocaleDateString('es-ES', { weekday: 'long' });

      if (!salesByBranch[branchName]) {
        salesByBranch[branchName] = {};
      }

      if (!salesByBranch[branchName][day]) {
        salesByBranch[branchName][day] = 0;
      }

      salesByBranch[branchName][day] += shift.amount_admin || 0;
    }
  }

  const daysOfWeek = ['lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado', 'domingo'];
  const result: BranchWeeklySales[] = Object.entries(salesByBranch).map(([branchName, sales]) => {
    return {
      branchName,
      sales: daysOfWeek.map(day => ({
        day,
        total: sales[day] || 0,
      })),
    };
  });

  return result;
};

export const getWeeklyAdminStats = async (): Promise<WeeklyAdminStats> => {
  const today = new Date();
  const dayOfWeek = today.getDay(); // Sunday = 0, Monday = 1, etc.
  
  // Calculate current week's Monday
  const diff = today.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1); // Adjust for Sunday
  const currentMonday = new Date(today.getTime());
  currentMonday.setDate(diff);
  currentMonday.setHours(0, 0, 0, 0);

  // Current week's Sunday
  const currentSunday = new Date(currentMonday.getTime());
  currentSunday.setDate(currentMonday.getDate() + 6);
  currentSunday.setHours(23, 59, 59, 999);

  // Previous week's Monday
  const previousMonday = new Date(currentMonday.getTime());
  previousMonday.setDate(currentMonday.getDate() - 7);
  previousMonday.setHours(0, 0, 0, 0);

  // Previous week's Sunday
  const previousSunday = new Date(previousMonday.getTime());
  previousSunday.setDate(previousMonday.getDate() + 6);
  previousSunday.setHours(23, 59, 59, 999);

  // Query shifts
  const { data: shifts, error: shiftsError } = await supabase
    .from('shifts')
    .select('start_time, amount_admin')
    .eq('is_deleted', false)
    .is('deleted_at', null)
    .gte('start_time', previousMonday.toISOString())
    .lte('start_time', currentSunday.toISOString());

  if (shiftsError) {
    console.error("Error fetching shifts for weekly admin stats:", shiftsError);
    throw shiftsError;
  }

  // Query completed rentals count for last week
  const { count: rentalsCount, error: rentalsError } = await supabase
    .from('rentals')
    .select('*', { count: 'exact', head: true })
    .eq('status', 'completed')
    .gte('start_date', previousMonday.toISOString())
    .lte('start_date', previousSunday.toISOString());

  if (rentalsError) {
    console.error("Error fetching rentals for weekly admin stats:", rentalsError);
    throw rentalsError;
  }

  let currentWeekProfit = 0;
  let previousWeekProfit = 0;

  if (shifts) {
    for (const shift of shifts) {
      const shiftDate = new Date(shift.start_time);
      if (shiftDate >= currentMonday && shiftDate <= currentSunday) {
        currentWeekProfit += shift.amount_admin || 0;
      } else if (shiftDate >= previousMonday && shiftDate <= previousSunday) {
        previousWeekProfit += shift.amount_admin || 0;
      }
    }
  }

  return {
    currentWeekProfit,
    previousWeekProfit,
    previousWeekRentals: rentalsCount || 0
  };
};